import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { RecordedAction, RecordedStep, GestureMode } from '../../Models/recorded-action-model';
import { RestService } from '../../Services/rest.service';
import { Literals } from '../../literals';

const STEP_NAME_KEYWORDS = [
  'open', 'openWindow', 'pause', 'selectFrame', 'selectWindow', 'close', 'ajaxWait', 'domWait',
  'type', 'select', 'submit', 'sendKeys', 'storeText', 'storeValue', 'verifySelectedValue',
  'assertText', 'verifyText', 'click', 'refresh', 'break', 'contextMenu', 'contextMenuAt',
  'doubleClick', 'doubleClickAt', 'dragAndDrop', 'dragAndDropToObject', 'waitForElementPresent',
  'waitForSomethingSelected', 'waitForVisible', 'verifyChecked', 'verifyEditable',
  'verifyElementNotPresent', 'verifyElementPresent', 'verifyNotEditable', 'mouseMoveAt',
  'mouseMove', 'mouseOver', 'assertAlert', 'chooseOkOnNextConfirmation', 'assertPrompt',
  'assertConfirmation', 'chooseCancelOnNextConfirmation', 'answerOnNextPrompt',
  'abstract', 'continue', 'for', 'new', 'switch', 'assert', 'default', 'goto*', 'package',
  'synchronized', 'boolean', 'do', 'if', 'private', 'this', 'double', 'implements',
  'protected', 'throw', 'byte', 'else', 'import', 'public', 'throws', 'case', 'enum*',
  'instanceof', 'return', 'transient', 'catch', 'extends', 'int', 'short', 'try', 'char',
  'final', 'interface', 'static', 'void', 'class', 'finally', 'long', 'strictfp**',
  'volatile', 'const*', 'float', 'native', 'super', 'while', 'Edit ', 'Save As '
];

@Component({
  selector: 'ze-recorded-actions',
  templateUrl: './recorded-actions.component.html',
  styleUrls: ['./recorded-actions.component.scss']
})
export class RecordedActionsComponent implements OnInit {

  @Input() recordedSteps: RecordedStep[] = [];
  @Input() isRecording = false;
  @Input() currentStepName = '';

  @Output() stepsCleared  = new EventEmitter<void>();
  @Output() stepRemoved   = new EventEmitter<string>();
  @Output() stepCleared   = new EventEmitter<string>();
  @Output() stepRenamed   = new EventEmitter<{ oldName: string; newName: string }>();
  @Output() actionRemoved = new EventEmitter<{ stepName: string; actionId: number }>();
  @Output() exported = new EventEmitter<void>();
  @Output() verifyElement = new EventEmitter<RecordedAction>();

  constructor(private restService: RestService) { }

  ngOnInit() {
    this.loadDbStepNames();
  }

  locatorTarget: RecordedAction | null = null;
  readonly gestureLiterals = Literals;

  renamingStep: string | null = null;
  renameValue = '';

  // ── Inline rename validation ───────────────────────────────────────────────
  renameError: 'specialCharacters' | 'keyword' | 'duplicate' | null = null;
  isLoadingRenameCheck = false;
  renameCheckLoadError = false;
  private dbStepNames: string[] = [];

  // ── Per-step save state ────────────────────────────────────────────────────
  stepSaveState: Record<string, 'idle' | 'saving' | 'success' | 'error'> = {};

  // ── Toast notification state ───────────────────────────────────────────────
  toastMessage: string | null = null;
  toastType: 'success' | 'error' = 'success';
  private toastTimer: any = null;

  get totalActions(): number {
    return this.recordedSteps.reduce((sum, s) => sum + s.actions.length, 0);
  }

  // ── Emitters ──────────────────────────────────────────────────────────────

  onClear()  {
    this.showConfirm('Do you really want to clear ALL steps? This will remove all recorded steps.', () => this.stepsCleared.emit());
  }
  confirmClearStep(stepName: string) {
    this.showConfirm(`Do you really want to clear all actions in step "${stepName}"?`, () => this.stepCleared.emit(stepName));
  }

  showConfirmModal = false;
  confirmMessage = '';
  private confirmAction: (() => void) | null = null;

  showConfirm(message: string, action: () => void) {
    this.confirmMessage = message;
    this.confirmAction  = action;
    this.showConfirmModal = true;
  }

  cancelConfirm() {
    this.showConfirmModal = false;
    this.confirmAction = null;
  }

  confirmOk() {
    if (this.confirmAction) {
      try { this.confirmAction(); } catch {}
    }
    this.cancelConfirm();
  }

  onRemoveStep(stepName: string) { this.stepRemoved.emit(stepName); }
  onRemoveAction(stepName: string, actionId: number) {
    this.actionRemoved.emit({ stepName, actionId });
  }

  toggleCollapse(step: RecordedStep) { step.collapsed = !step.collapsed; }

  onExport() {
    const blob = new Blob(
      [JSON.stringify(this.recordedSteps, null, 2)],
      { type: 'application/json' }
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `recorded-steps-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
    this.exported.emit();
  }

  // ── Per-step Save to Canvas ───────────────────────────────────────────────

  getSaveState(stepName: string): 'idle' | 'saving' | 'success' | 'error' {
    return this.stepSaveState[stepName] || 'idle';
  }

  onSaveStep(step: RecordedStep, event: MouseEvent) {
    event.stopPropagation();

    const stepName = (step.name || '').trim();
    if (!stepName) {
      this.showToast('error', 'Cannot save an unnamed step. Please give the step a name first.');
      return;
    }

    if (!step.actions || step.actions.length === 0) {
      this.showToast('error', 'Cannot save an empty step. Record at least one action before saving.');
      return;
    }

    const projectId = localStorage.getItem('projectIdDb2');
    if (!projectId) {
      this.showToast('error', 'No project selected. Please open a project first.');
      return;
    }

    const alreadyExistsInCanvas = this.dbStepNames.some(
      n => n.trim() === stepName
    );

    const alreadyExistsInRecorder = this.recordedSteps.some(
      s => s.name.trim() === stepName && s !== step
    );

    if (alreadyExistsInRecorder) {
      this.showToast('error', `A step named "${stepName}" already exists in the recorder page. Please rename the step before saving.`);
      return;
    }

    if (alreadyExistsInCanvas) {
      this.showToast('error', `A step named "${stepName}" already exists in Canvas. Please rename the step before saving.`);
      return;
    }

    const payload = {
      projectId,
      steps: [{
        name: stepName,
        commandsteps: step.actions.map((action, idx) => ({
          action: this.mapAction(action),
          value: this.mapValue(action),
          webobjects: this.buildWebobjects(action),
          webobjects2: [],
          seq_no: idx,
          ispassword: false
        }))
      }]
    };

    this.stepSaveState[step.name] = 'saving';

    this.restService.put('tempObject', payload).subscribe(
      (_res: any) => {
        this.stepSaveState[step.name] = 'idle';
        this.showToast('success', `"${step.name}" saved to Canvas successfully`);
        this.dbStepNames.push(stepName);
        this.exported.emit();
      },
      (_err: any) => {
        this.stepSaveState[step.name] = 'idle';
        this.showToast('error', `Failed to save "${step.name}". Please try again.`);
      }
    );
  }

  // ── Action name mapping ───────────────────────────────────────────────────
  /**
   * Maps internal GestureMode names → payload action strings
   * matching the exact values the backend expects.
   */
  private mapAction(action: RecordedAction): string {
    const map: Record<string, string> = {
      [Literals.TAP]: Literals.TAP,
      'doubletap': 'double tap',
      'longpress': 'long press',
      [Literals.TYPE]: Literals.TAP_AND_TYPE,
      'swipe': 'swipe',
      'twofinger-swipe': 'two fingers swipe',
      'threefinger-swipe': 'three fingers swipe',
      'zoom-in': 'zoom in',
      'zoom-out': 'zoom out',
      'get-text': 'get text',
      'get-value': 'get value',
      'verify-text': 'verify text',
      'assert-text': 'assert text',
      'verify-text-contains': 'verify text contains',
      'assert-text-contains': 'assert text contains',
      'open': 'open',
      'press-home': 'press home',
      'press-back': 'press back',
      'press-recents': 'press recent apps',
      'press-volumeup': 'press volume up',
      'press-volumedown': 'press volume down',
      'keyboard-action': 'keyboard action',
      'slider': 'slider swipe',
    };
    return map[action.action] || action.action;
  }

  // ── Value mapping ─────────────────────────────────────────────────────────
  private mapValue(action: RecordedAction): string {
    // Swipe: "startX,startY:endX,endY"
    const swipeActions = ['swipe', 'twofinger-swipe', 'threefinger-swipe'];
    if (swipeActions.includes(action.action)) {
      const from = action.coordinates;
      const to = action.swipeTo;
      if (from && to) return `${from.x},${from.y}:${to.x},${to.y}`;
      if (from) return `${from.x},${from.y}`;
    }

    // Zoom: "x,y"
    if (action.action === 'zoom-in' || action.action === 'zoom-out') {
      if (action.coordinates) return `${action.coordinates.x},${action.coordinates.y}`;
    }

    // Long press: strip 'ms' suffix — backend expects plain number e.g. "2134" not "2134ms"
    if (action.action === 'longpress' && action.value) {
      return action.value.replace(/ms$/i, '');
    }

    if (action.action === 'slider' && action.sliderPercent) {
      const { fromPct, toPct } = action.sliderPercent;
      return `${Math.round(fromPct * 100)}%→${Math.round(toPct * 100)}%`;
    }

    // Hardware keys: empty value
    const hwKeys = ['press-home', 'press-back', 'press-recents', 'press-volumeup', 'press-volumedown'];
    if (hwKeys.includes(action.action)) return '';

    // Keyboard IME actions: value is the key name (search, done, go, etc.)
    if (action.action === 'keyboard-action') return action.value || '';

    return action.value || '';
  }

  // ── Webobjects builder ────────────────────────────────────────────────────
  /**
   * Builds the webobjects array matching the exact format in the payload sample:
   *   "accessibility id=Views"
   *   "id=android:id/text1"
   *   "//[contains(@text,'Views')]"
   *   absolute xpath
   *   relative xpath
   *   text-based xpath
   *   resource-id xpath
   *   content-desc xpath
   *   "class=android.widget.TextView"
   *   "X: 131, Y: 1777"
   *
   * Coordinate-only actions (swipe, zoom) get no webobjects — empty array.
   */
  private buildWebobjects(action: RecordedAction): string[] {
    const coordOnly = [
      'swipe', 'twofinger-swipe', 'threefinger-swipe', 'zoom-in', 'zoom-out',
      'press-home', 'press-back', 'press-recents', 'press-volumeup', 'press-volumedown',
      'keyboard-action'
    ];
    if (coordOnly.includes(action.action)) {
      return [];
    }

    const el = action.element;
    const webobjects: string[] = [];

    if (!el) {
      // No element info — fall back to coordinates
      if (action.coordinates) {
        webobjects.push(`X: ${action.coordinates.x}, Y: ${action.coordinates.y}`);
      }
      return webobjects;
    }

    // 0. webobject_name — human-readable display name derived from element properties
    //    Priority: text → accessibilityId → resourceId (last segment) → className (last segment)
    const webobjectName: string =
      el.text ? el.text :
        el.accessibilityId ? el.accessibilityId :
          el.resourceId ? (el.resourceId.split('/').pop() || el.resourceId) :
            el.className ? (el.className.split('.').pop() || el.className) :
              '';
    if (webobjectName) {
      webobjects.push(`webobject_name=${webobjectName}`);
    }

    // 1. accessibility id
    if (el.accessibilityId) {
      webobjects.push(`accessibility id=${el.accessibilityId}`);
    }

    // 2. resource-id
    if (el.resourceId) {
      webobjects.push(`id=${el.resourceId}`);
    }

    // 3. text contains xpath
    if (el.text) {
      webobjects.push(`//[contains(@text,'${el.text}')]`);
    }

    // 4. absolute xpath
    if (el.xpaths.absoluteXpath) {
      webobjects.push(el.xpaths.absoluteXpath);
    }

    // 5. relative xpath (text+resource-id combo)
    if (el.xpaths.relativeXpath) {
      webobjects.push(el.xpaths.relativeXpath);
    }

    // 6. text-based xpath
    if (el.xpaths.textBasedXpath) {
      webobjects.push(el.xpaths.textBasedXpath);
    }

    // 7. resource-id xpath
    if (el.xpaths.resourceIdXpath) {
      webobjects.push(el.xpaths.resourceIdXpath);
    }

    // 8. content-desc / accessibility xpath
    if (el.xpaths.contentDescXpath) {
      webobjects.push(el.xpaths.contentDescXpath);
    }

    // 9. class
    if (el.className) {
      webobjects.push(`class=${el.className}`);
    }

    // 10. coordinates (always last)
    if (action.coordinates) {
      webobjects.push(`X: ${action.coordinates.x}, Y: ${action.coordinates.y}`);
    }

    return webobjects;
  }

  // ── Inline rename ─────────────────────────────────────────────────────────

  startRename(stepName: string, event: MouseEvent) {
    event.stopPropagation();
    this.renamingStep = stepName;
    this.renameValue = stepName;
    this.renameError = null;
    this.renameCheckLoadError = false;
    this.loadDbStepNames();
  }

  private loadDbStepNames(): void {
    const projectId = localStorage.getItem('projectIdDb2');
    if (!projectId) { this.dbStepNames = []; return; }

    this.isLoadingRenameCheck = true;
    this.renameCheckLoadError = false;

    this.restService.get('projectStepsdb2', projectId).subscribe(
      (response: any) => {
        const steps: any[] = Array.isArray(response) ? response
          : Array.isArray(response.data) ? response.data
            : [];
        this.dbStepNames = steps.map((s: any) => s.name || s.stepName || '').filter(Boolean);
        this.isLoadingRenameCheck = false;
        if (this.renameValue) this.validateRename(this.renameValue);
      },
      (_err: any) => {
        this.isLoadingRenameCheck = false;
        this.renameCheckLoadError = true;
      }
    );
  }

  validateRename(value: string): void {
    this.renameError = null;
    if (!value) return;

    const trimmedValue = value.trim();
    const trimmedRenaming = (this.renamingStep || '').trim();

    if (!/^[A-Za-z_][A-Za-z0-9_\- ]*$/.test(trimmedValue)) {
      this.renameError = 'specialCharacters';
      return;
    }

    if (STEP_NAME_KEYWORDS.some(keyword => keyword.trim() === trimmedValue)) {
      this.renameError = 'keyword';
      return;
    }

    if (this.recordedSteps.some(
      s => s.name.trim() === trimmedValue
        && s.name.trim() !== trimmedRenaming
    )) {
      this.renameError = 'duplicate';
      return;
    }

    if (this.dbStepNames.some(
      n => n.trim() === trimmedValue
        && n.trim() !== trimmedRenaming
    )) {
      this.renameError = 'duplicate';
    }
  }

  get renameErrorMessage(): string {
    switch (this.renameError) {
      case 'specialCharacters': return 'Must start with a letter or underscore. Only letters, digits, _, -, or spaces allowed.';
      case 'keyword': return 'This name is a reserved keyword and cannot be used.';
      case 'duplicate': return 'A step with this name already exists.';
      default: return '';
    }
  }

  get isRenameValid(): boolean {
    return !!this.renameValue.trim() && this.renameError === null && !this.isLoadingRenameCheck;
  }

  confirmRename() {
    if (!this.isRenameValid) return;
    const trimmed = this.renameValue.trim();
    if (trimmed !== this.renamingStep) {
      this.stepRenamed.emit({ oldName: this.renamingStep!, newName: trimmed });
    }
    this._clearRename();
  }

  cancelRename() { this._clearRename(); }

  private _clearRename() {
    this.renamingStep = null;
    this.renameValue = '';
    this.renameError = null;
    this.isLoadingRenameCheck = false;
    this.renameCheckLoadError = false;
  }

  // ── Locator dialog ────────────────────────────────────────────────────────

  openLocators(action: RecordedAction, event: MouseEvent) {
    event.stopPropagation();
    this.locatorTarget = action;
  }

  closeLocators() { this.locatorTarget = null; }

  // ── Display helpers ───────────────────────────────────────────────────────

  // ── Toast helper ────────────────────────────────────────────────────────────
  showToast(type: 'success' | 'error', message: string): void {
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.toastType = type;
    this.toastMessage = message;
    this.toastTimer = setTimeout(() => {
      this.toastMessage = null;
      this.toastTimer = null;
    }, 3500);
  }

  isCoordAction(action: GestureMode | string): boolean {
    return ['swipe', 'twofinger-swipe', 'threefinger-swipe', 'zoom-in', 'zoom-out'].includes(action as string);
  }

  isHardwareKey(action: GestureMode | string): boolean {
    return ['press-home', 'press-back', 'press-recents', 'press-volumeup', 'press-volumedown', 'keyboard-action'].includes(action as string);
  }

  isKeyboardAction(action: GestureMode | string): boolean {
    return (action as string) === 'keyboard-action';
  }

  isOpenCmd(action: GestureMode | string): boolean { return action === 'open'; }

  getPrimaryLocator(action: RecordedAction): string {
    const el = action.element;
    if (!el) return action.coordinates ? `${action.coordinates.x},${action.coordinates.y}` : '';
    return el.accessibilityId || el.resourceId || el.xpath || el.className || el.text || '';
  }

  getTargetDisplayName(action: RecordedAction): string {
    const el = action.element;
    if (!el) return `(${action.coordinates.x || 0}, ${action.coordinates.y || 0})`;
    if (el.accessibilityId) return el.accessibilityId;
    if (el.resourceId) return el.resourceId.split('/').pop() || el.resourceId;
    if (el.text) return el.text;
    if (el.className) return el.className.split('.').pop() || el.className;
    if (el.xpath) {
      const quoted = el.xpath.match(/'([^']+)'/g);
      if (quoted.length) return quoted[quoted.length - 1].replace(/'/g, '');
      const parts = el.xpath.split('/').filter(Boolean);
      let seg = parts.pop() || el.xpath;
      if (seg.includes('[')) seg = seg.split('[')[0];
      return seg;
    }
    return action.coordinates ? `(${action.coordinates.x}, ${action.coordinates.y})` : '';
  }

  getValueDisplay(action: RecordedAction): string {
    if (this.isOpenCmd(action.action)) return this.getPrimaryLocator(action);
    if (action.action === 'slider' && (action as any).sliderPercent) {
      const { fromPct, toPct } = (action as any).sliderPercent;
      return `${Math.round(fromPct * 100)}:${Math.round(toPct * 100)}`;
    }
    return action.value || '';
  }

  copyToClipboard(text: string) { navigator.clipboard.writeText(text).catch(() => { }); }

  isFlutterElement(element: RecordedAction['element']): boolean {
    if (!element.className) return false;
    return !element.className.includes('.');
  }

  getModeIcon(mode: GestureMode | string): string {
    const icons: Record<string, string> = {
      tap: '☝️', doubletap: '✌️', longpress: '✋', swipe: '👆',
      type: '⌨️', 'zoom-in': '🔍+', 'zoom-out': '🔍−', 'twofinger-swipe': '👐', open: '🌐',
      'get-text': '', 'get-value': ''
    };
    return icons[mode] || '?';
  }

  getActionColor(action: GestureMode | string): string {
    const colors: Record<string, string> = {
      tap: '#6c63ff', doubletap: '#8b5cf6', longpress: '#f59e0b',
      swipe: '#22d3ee', type: '#4ade80',
      'zoom-in': '#f97316', 'zoom-out': '#fb923c', 'twofinger-swipe': '#e879f9',
      open: '#38bdf8', 'get-text': '#06b6d4', 'get-value': '#0ea5e9'
    };
    return colors[action] || '#6c63ff';
  }

  onRowDoubleClick(action: RecordedAction) {
    if (!action.element) return;
    this.verifyElement.emit(action);
  }

}